import { useRef, useState } from "react";
import ReactCrop, {
  type Crop,
  type PixelCrop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { RotateCw, Square, Crop as CropIcon } from "lucide-react";

const LILAC = "var(--caileve-lilac)";
const TAUPE = "var(--caileve-muted)";
const BG = "var(--caileve-bg)";
const INK = "var(--caileve-ink)";
const SOFT = "var(--caileve-soft)";

type Props = {
  file: File;
  onConfirm: (cropped: File) => void;
  onCancel: () => void;
};

function centerSquare(width: number, height: number): Crop {
  return centerCrop(
    makeAspectCrop({ unit: "%", width: 90 }, 1, width, height),
    width,
    height,
  );
}

async function renderCroppedFile(
  image: HTMLImageElement,
  crop: PixelCrop,
  rotation: number,
  filename: string,
): Promise<File> {
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const cropW = Math.max(1, Math.round(crop.width * scaleX));
  const cropH = Math.max(1, Math.round(crop.height * scaleY));

  // Output canvas dimensions depend on rotation: swap on 90/270.
  const rot = ((rotation % 360) + 360) % 360;
  const swap = rot === 90 || rot === 270;
  const outW = swap ? cropH : cropW;
  const outH = swap ? cropW : cropH;

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");

  ctx.imageSmoothingQuality = "high";
  ctx.translate(outW / 2, outH / 2);
  ctx.rotate((rot * Math.PI) / 180);
  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    cropW,
    cropH,
    -cropW / 2,
    -cropH / 2,
    cropW,
    cropH,
  );

  const base = filename.replace(/\.[^.]+$/, "") || "photo";
  return await new Promise<File>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Could not produce cropped image"));
        resolve(new File([blob], `${base}.jpg`, { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92,
    );
  });
}

export function PhotoCropper({ file, onConfirm, onCancel }: Props) {
  const [src] = useState(() => URL.createObjectURL(file));
  const [crop, setCrop] = useState<Crop>();
  const [completed, setCompleted] = useState<PixelCrop>();
  const [rotation, setRotation] = useState(0);
  const [square, setSquare] = useState(false);
  const [busy, setBusy] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const initial = square
      ? centerSquare(width, height)
      : ({ unit: "%", x: 5, y: 5, width: 90, height: 90 } as Crop);
    setCrop(initial);
  };

  const toggleSquare = () => {
    setSquare((prev) => {
      const next = !prev;
      const img = imgRef.current;
      if (img) {
        setCrop(
          next
            ? centerSquare(img.width, img.height)
            : ({ unit: "%", x: 5, y: 5, width: 90, height: 90 } as Crop),
        );
      }
      return next;
    });
  };

  const rotate = () => setRotation((r) => (r + 90) % 360);

  const handleUse = async () => {
    const img = imgRef.current;
    if (!img || !completed || completed.width < 4 || completed.height < 4) {
      // Fall back to full image when no crop yet.
      onConfirm(file);
      return;
    }
    setBusy(true);
    try {
      const out = await renderCroppedFile(img, completed, rotation, file.name);
      onConfirm(out);
    } catch {
      onConfirm(file);
    } finally {
      setBusy(false);
      URL.revokeObjectURL(src);
    }
  };

  const handleCancel = () => {
    URL.revokeObjectURL(src);
    onCancel();
  };

  return (
    <div
      className="rounded-md p-3 space-y-3"
      style={{ backgroundColor: BG, border: `1px solid ${SOFT}` }}
    >
      <style>{`
        .caileve-crop .ReactCrop__crop-selection { border-color: ${LILAC}; box-shadow: 0 0 0 9999em ${INK}33; }
        .caileve-crop .ReactCrop__drag-handle::after { background: ${LILAC}; border-color: ${BG}; }
        .caileve-crop .ReactCrop__rule-of-thirds-vt::before,
        .caileve-crop .ReactCrop__rule-of-thirds-vt::after,
        .caileve-crop .ReactCrop__rule-of-thirds-hz::before,
        .caileve-crop .ReactCrop__rule-of-thirds-hz::after { background: ${LILAC}80; }
      `}</style>
      <div className="caileve-crop flex items-center justify-center max-h-[60vh] overflow-hidden">
        <ReactCrop
          crop={crop}
          onChange={(c) => setCrop(c)}
          onComplete={(c) => setCompleted(c)}
          aspect={square ? 1 : undefined}
          ruleOfThirds
        >
          <img
            ref={imgRef}
            src={src}
            alt="Photo being cropped"
            onLoad={onImageLoad}
            style={{
              maxHeight: "60vh",
              maxWidth: "100%",
              transform: `rotate(${rotation}deg)`,
              transformOrigin: "center center",
            }}
          />
        </ReactCrop>
      </div>

      <div className="flex items-center justify-center gap-2">
        <button
          type="button"
          onClick={toggleSquare}
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-xs uppercase tracking-[0.2em]"
          style={{
            backgroundColor: square ? LILAC : SOFT,
            color: square ? BG : INK,
          }}
          aria-pressed={square}
        >
          {square ? <Square className="h-3.5 w-3.5" /> : <CropIcon className="h-3.5 w-3.5" />}
          {square ? "Square" : "Free"}
        </button>
        <button
          type="button"
          onClick={rotate}
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-xs uppercase tracking-[0.2em]"
          style={{ backgroundColor: SOFT, color: INK }}
          aria-label="Rotate 90 degrees"
        >
          <RotateCw className="h-3.5 w-3.5" /> Rotate
        </button>
      </div>

      <div className="flex items-center justify-between pt-1">
        <button
          type="button"
          onClick={handleCancel}
          disabled={busy}
          className="text-xs uppercase tracking-[0.25em] underline-offset-4 hover:underline disabled:opacity-50"
          style={{ color: TAUPE }}
        >
          Retake
        </button>
        <button
          type="button"
          onClick={handleUse}
          disabled={busy}
          className="h-10 px-5 rounded-md text-xs uppercase tracking-[0.25em] disabled:opacity-60"
          style={{ backgroundColor: LILAC, color: BG }}
        >
          {busy ? "Preparing…" : "Use Photo"}
        </button>
      </div>
    </div>
  );
}